import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Zap, RefreshCw, X, ShieldCheck, AlertCircle, ArrowRight, Building2 } from 'lucide-react';

interface NicheResetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  newNiche: string;
  currentCount?: number;
}

export default function NicheResetModal({
  isOpen,
  onClose,
  onConfirm,
  newNiche
}: NicheResetModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-100 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-zinc-950/70 backdrop-blur-md"
          />

          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 15 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 15 }}
            className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden border border-zinc-100"
          >
            {/* Header com Gradiente Sofisticado */}
            <div className="bg-linear-to-r from-[#004B87] to-indigo-700 p-6 text-white relative">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-white/10 rounded-2xl backdrop-blur-md border border-white/20">
                    <RefreshCw className="h-6 w-6 text-amber-300" />
                  </div>
                  <div>
                    <h2 className="text-lg font-black tracking-wide">Recomeço Limpo de Nicho</h2>
                    <p className="text-xs text-blue-100/90 font-medium">Preparando seu R-MAX para o novo segmento</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/15 rounded-xl transition-colors text-white/80 hover:text-white cursor-pointer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Tag com garantia de assinatura ativa */}
              <div className="mt-4 inline-flex items-center gap-2 bg-white/15 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-white/20 text-xs font-bold text-amber-200">
                <ShieldCheck className="h-4 w-4 text-emerald-400" />
                <span>Mesmo Login & Assinatura Ativa</span>
                <span className="opacity-40">•</span>
                <span className="text-white/90 font-normal">
                  Sem taxas adicionais
                </span>
              </div>
            </div>

            {/* Conteúdo Principal do Modal */}
            <div className="p-6 sm:p-8 space-y-5">

              {/* Destaque do Novo Nicho */}
              <div className="bg-blue-50/70 border border-blue-100 rounded-2xl p-4 flex items-center justify-between">
                <div className="space-y-0.5">
                  <span className="text-[10px] font-black uppercase tracking-widest text-[#004B87]">Novo Segmento Escolhido</span>
                  <p className="text-base font-bold text-zinc-900">{newNiche}</p>
                </div>
                <div className="p-2.5 bg-[#004B87] text-white rounded-xl shadow-md">
                  <Zap className="h-5 w-5" />
                </div>
              </div>

              {/* AVISO DA ESCALADA DE CRESCIMENTO */}
              <div className="bg-amber-50/80 border border-amber-200/80 rounded-2xl p-3.5 flex items-start gap-2.5 text-xs text-amber-900">
                <Building2 className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-bold">Está apenas expandindo seu negócio atual?</p>
                  <p className="text-amber-800 leading-relaxed text-[11px]">
                    Se você está apenas aumentando sua equipe, abrindo loja física ou subindo de plano, <strong>você não precisa apagar nada</strong>! O R-Max preserva todo o seu histórico.
                  </p>
                </div>
              </div>

              {/* Explicação da Limpeza para Mudança Total de Ramo */}
              <div className="space-y-1.5">
                <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-widest">Por que limparemos os dados anteriores?</h4>
                <p className="text-xs text-zinc-600 leading-relaxed bg-zinc-50 p-3.5 rounded-xl border border-zinc-100">
                  Para garantir que os custos fixos, insumos e orçamentos da sua <strong>nova atividade fiquem 100% precisos</strong>, os dados do ramo anterior serão limpos para você não misturar contas antigas.
                </p>
              </div>

              {/* Lista de itens resetados */}
              <div className="bg-rose-50/50 border border-rose-100 rounded-2xl p-3.5 space-y-2">
                <div className="flex items-center gap-2 text-rose-700 font-bold text-xs">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  <span>Serão limpos para o novo segmento:</span>
                </div>
                <ul className="text-[11px] text-rose-900/80 space-y-1 pl-5 list-disc font-medium">
                  <li>Orçamentos salvos do ramo anterior</li>
                  <li>Lista de insumos, materiais e receitas antigas</li>
                  <li>Fichas de serviços do segmento encerrado</li>
                </ul>
              </div>

              {/* Nota sobre dois negócios paralelos */}
              <p className="text-[10px] text-zinc-400 text-center">
                * Deseja manter os dois negócios operando simultaneamente? Assine uma conta adicional para sua 2ª empresa.
              </p>

              {/* Botões de Ação */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <button
                  onClick={onClose}
                  className="py-3 px-4 rounded-2xl border border-zinc-200 text-zinc-700 font-bold text-xs hover:bg-zinc-50 active:scale-95 transition-all cursor-pointer"
                >
                  Manter Atividade Atual
                </button>
                <button
                  onClick={onConfirm}
                  className="py-3 px-4 rounded-2xl bg-linear-to-r from-[#004B87] to-indigo-600 text-white font-bold text-xs shadow-lg shadow-blue-900/20 hover:from-blue-900 hover:to-indigo-700 active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
                >
                  <span>Confirmar Limpeza e Iniciar</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>

            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
